/**
 * Janko Piano - Configuration Module
 * Central repository for app settings, constants, and state
 * @version 0.3.3
 */

// =============================================================================
// APP CONFIGURATION
// =============================================================================

export const CONFIG = {
    // Layout
    rows: 6,
    baseCols: 10,
    totalCols: 88,
    zoom: 1.0,
    scrollPos: 1.0,
    gap: 4,
    
    // Audio Effects
    reverbAmt: 0,
    chorusAmt: 0,
    distortionAmt: 0,
    echoAmt: 0,
    
    // Features
    keyboardVisuals: false,
    chordMode: 'off',
    splitOctave: false,
    
    // Appearance
    keyShape: 'original',
    colorScheme: 'classic',
    colors: { 
        white: '#e0e0e0', 
        black: '#1f1f1f', 
        active: '#FF9F0A' 
    },
    
    // User State
    isPaidUser: false,
    
    // Device Detection
    detectedType: 'desktop',
    simulatedType: 'auto',
    
    // Split Octave Tuning
    splitParams: {
        HexagonWaveHeight: 0.17,
        CircleWaveHeight: 0.07
    },
    
    // Runtime (set during init)
    appVersion: '0.3.3',
    centerOffset: 0,
    activeTotalCols: 42.5,
    rotationInitialized: false,
    
    // Performance
    isLowEnd: false,
    maxDpr: 3,  // Cap DPR for performance
    maxPolyphony: Infinity,  // Max simultaneous notes (16 on low-end, unlimited on high-end)
    maxRows: 12,  // Max selectable rows (reduced on low-end small screens)
    
    // MIDI
    midiInputEnabled: false,
    midiOutputEnabled: false,
    midiInputDeviceId: null,
    midiOutputDeviceId: null,
    
    // MIDI Playback Filtering
    visualizeAllChannels: true,  // Show all instruments or filter by current channel
    currentPlaybackChannel: null, // null = show all, 0-15 = show specific channel
    midiChannel: 0,
    debugMode: false,
};

// =============================================================================
// CHORD INTERVALS
// =============================================================================

export const CHORD_INTERVALS = {
    off: [0],
    major: [0, 4, 7],
    minor: [0, 3, 7],
    sus2: [0, 2, 7],
    sus4: [0, 5, 7],
    aug: [0, 4, 8],
    dim: [0, 3, 6],
    '7th': [0, 4, 7, 10],
    power: [0, 7, 12]
};

// =============================================================================
// GM PRESET MAP (SpessaSynth)
// =============================================================================

export const PRESETS = {
    piano: 0,
    bright_piano: 1,
    bass: 32,
    strings: 48,
    elec_guitar: 27,
    rhodes: 4,
    guitar: 25,
    organ: 16,
    synth: 81,
    drums: 'drums',
    trumpet: 56,
    violin: 40,
    flute: 73,
    synth_pad: 88,
    cello: 42,
    dist_guitar: 30,
    saxophone: 65,
    vibes: 11,
    marimba: 12,
    harmonica: 22
};

// =============================================================================
// INSTRUMENT METADATA
// =============================================================================

export const INSTRUMENT_NAMES = {
    piano: 'Grand Piano',
    bright_piano: 'Bright Piano',
    bass: 'Acoustic Bass',
    strings: 'Strings',
    elec_guitar: 'Elec. Guitar',
    rhodes: 'Rhodes',
    guitar: 'Acoustic',
    organ: 'Organ',
    synth: 'Synth Lead',
    drums: 'Drums',
    trumpet: 'Trumpet',
    violin: 'Violin',
    flute: 'Flute',
    synth_pad: 'Synth Pad',
    cello: 'Cello',
    dist_guitar: 'Dist. Guitar',
    saxophone: 'Saxophone',
    vibes: 'Xylophone',
    marimba: 'Marimba',
    harmonica: 'Harmonica'
};

export const INSTRUMENT_IDS = [
    'piano', 'bright_piano', 'bass', 'strings', 'elec_guitar',
    'rhodes', 'guitar', 'organ', 'synth', 'trumpet',
    'violin', 'flute', 'synth_pad', 'cello', 'dist_guitar',
    'saxophone', 'vibes', 'marimba', 'harmonica', 'drums'
];

// Map instrument IDs to icon filenames
export const INSTRUMENT_ICONS = {
    piano: 'grand_piano',
    bright_piano: 'bright_piano',
    bass: 'acoustic_bass',
    strings: 'string_ensemble',
    elec_guitar: 'electric_guitar',
    rhodes: 'rhodes',
    guitar: 'guitar',
    organ: 'organ',
    synth: 'synth',
    drums: 'drums',
    trumpet: 'trumpet',
    violin: 'violin',
    flute: 'flute',
    synth_pad: 'synth_pad',
    cello: 'cello',
    dist_guitar: 'dist_guitar',
    saxophone: 'saxophone',
    vibes: 'vibes',
    marimba: 'marimba',
    harmonica: 'harmonica'
};

// 20 Colors - Full Spectrum (18° apart, starting at 30° Orange for Piano)
export const INSTRUMENT_COLORS = {
    piano: '#FF9900',        // 30° - Orange (Main Color)
    bright_piano: '#FFD700', // 48° - Gold
    bass: '#E6FF00',         // 66° - Yellow-Green
    strings: '#99FF00',      // 84° - Lime Green
    elec_guitar: '#4DFF00',  // 102° - Green
    rhodes: '#00FF00',       // 120° - Pure Green
    guitar: '#00FF4D',       // 138° - Green-Cyan
    organ: '#00FF99',        // 156° - Spring Green
    synth: '#00FFE6',        // 174° - Turquoise
    trumpet: '#00E6FF',      // 192° - Cyan
    violin: '#0099FF',       // 210° - Sky Blue
    flute: '#004DFF',        // 228° - Blue
    synth_pad: '#0000FF',    // 246° - Pure Blue
    cello: '#4D00FF',        // 264° - Blue-Violet
    dist_guitar: '#9900FF',  // 282° - Violet
    saxophone: '#E600FF',    // 300° - Magenta
    vibes: '#FF00E6',        // 318° - Pink-Magenta
    marimba: '#FF0099',      // 336° - Hot Pink
    harmonica: '#FF004D',    // 354° - Red-Pink
    drums: '#FF1A00'         // 12° - Red-Orange
};

// CSS Filter mappings for SVG icon coloring - Shifted Spectrum (Orange-based)
export const COLOR_FILTERS = {
    '#FF9900': 'invert(55%) sepia(100%) saturate(3000%) hue-rotate(20deg) brightness(105%) contrast(105%)',
    '#FFD700': 'invert(80%) sepia(100%) saturate(5000%) hue-rotate(5deg) brightness(110%) contrast(100%)',
    '#E6FF00': 'invert(95%) sepia(100%) saturate(5000%) hue-rotate(15deg) brightness(105%) contrast(105%)',
    '#99FF00': 'invert(80%) sepia(90%) saturate(2000%) hue-rotate(70deg) brightness(105%) contrast(100%)',
    '#4DFF00': 'invert(70%) sepia(100%) saturate(2500%) hue-rotate(90deg) brightness(105%) contrast(105%)',
    '#00FF00': 'invert(65%) sepia(100%) saturate(3000%) hue-rotate(110deg) brightness(105%) contrast(105%)',
    '#00FF4D': 'invert(65%) sepia(90%) saturate(2500%) hue-rotate(130deg) brightness(105%) contrast(100%)',
    '#00FF99': 'invert(70%) sepia(85%) saturate(2000%) hue-rotate(145deg) brightness(105%) contrast(100%)',
    '#00FFE6': 'invert(75%) sepia(90%) saturate(1800%) hue-rotate(160deg) brightness(105%) contrast(100%)',
    '#00E6FF': 'invert(70%) sepia(100%) saturate(2000%) hue-rotate(175deg) brightness(105%) contrast(100%)',
    '#0099FF': 'invert(55%) sepia(100%) saturate(3000%) hue-rotate(195deg) brightness(105%) contrast(105%)',
    '#004DFF': 'invert(35%) sepia(100%) saturate(5000%) hue-rotate(220deg) brightness(105%) contrast(110%)',
    '#0000FF': 'invert(20%) sepia(100%) saturate(7500%) hue-rotate(240deg) brightness(100%) contrast(115%)',
    '#4D00FF': 'invert(30%) sepia(100%) saturate(6000%) hue-rotate(260deg) brightness(100%) contrast(115%)',
    '#9900FF': 'invert(40%) sepia(100%) saturate(5000%) hue-rotate(280deg) brightness(105%) contrast(110%)',
    '#E600FF': 'invert(50%) sepia(100%) saturate(4000%) hue-rotate(295deg) brightness(105%) contrast(110%)',
    '#FF00E6': 'invert(45%) sepia(100%) saturate(5000%) hue-rotate(310deg) brightness(105%) contrast(110%)',
    '#FF0099': 'invert(35%) sepia(100%) saturate(6000%) hue-rotate(330deg) brightness(105%) contrast(110%)',
    '#FF004D': 'invert(25%) sepia(100%) saturate(7000%) hue-rotate(350deg) brightness(105%) contrast(115%)',
    '#FF1A00': 'invert(30%) sepia(100%) saturate(6000%) hue-rotate(5deg) brightness(105%) contrast(115%)'
};

// =============================================================================
// CHORD PATTERN PREVIEWS (HTML)
// =============================================================================

export const CHORD_PATTERNS = {
    'off': '<span class="chord-preview-label">Single Note</span>',
    'major': '<div class="mini-grid"><div class="mini-row shift"><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key chord"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div></div><div class="mini-row"><div class="mini-key root"></div><div class="mini-key"></div><div class="mini-key chord"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div></div></div>',
    'minor': '<div class="mini-grid"><div class="mini-row shift"><div class="mini-key"></div><div class="mini-key chord"></div><div class="mini-key"></div><div class="mini-key chord"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div></div><div class="mini-row"><div class="mini-key root"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div></div></div>',
    'sus2': '<div class="mini-grid"><div class="mini-row shift"><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key chord"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div></div><div class="mini-row"><div class="mini-key root"></div><div class="mini-key chord"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div></div></div>',
    'sus4': '<div class="mini-grid"><div class="mini-row shift"><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key chord"></div><div class="mini-key chord"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div></div><div class="mini-row"><div class="mini-key root"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div></div></div>',
    'aug': '<div class="mini-grid"><div class="mini-row shift"><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div></div><div class="mini-row"><div class="mini-key root"></div><div class="mini-key"></div><div class="mini-key chord"></div><div class="mini-key"></div><div class="mini-key chord"></div><div class="mini-key"></div><div class="mini-key"></div></div></div>',
    'dim': '<div class="mini-grid"><div class="mini-row shift"><div class="mini-key"></div><div class="mini-key chord"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div></div><div class="mini-row"><div class="mini-key root"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key chord"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div></div></div>',
    '7th': '<div class="mini-grid"><div class="mini-row shift"><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key chord"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div></div><div class="mini-row"><div class="mini-key root"></div><div class="mini-key"></div><div class="mini-key chord"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key chord"></div><div class="mini-key"></div></div></div>',
    'power': '<div class="mini-grid"><div class="mini-row shift"><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key chord"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div></div><div class="mini-row"><div class="mini-key root"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key"></div><div class="mini-key chord"></div></div></div>'
};

// =============================================================================
// SVG ICONS
// =============================================================================

export const SVG_ICONS = {
    piano: '<path d="M10 20 C 10 10, 20 5, 30 5 C 45 5, 50 15, 55 25 L 55 45 L 10 45 Z M10 45 L 10 55 M18 45 L 18 55 M26 45 L 26 55 M50 45 L 50 55 M10 45 H 55 V 35 H 10 Z M12 55 L 12 60 M20 55 L 20 60 M48 55 L 48 60" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
    rhodes: '<rect x="8" y="20" width="48" height="24" rx="2" fill="none" stroke="currentColor" stroke-width="2"/><path d="M8 32 H 56 M16 32 V 44 M24 32 V 44 M32 32 V 44 M40 32 V 44 M48 32 V 44 M12 44 L 8 56 M52 44 L 56 56" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M14 25 L 20 25 M24 25 L 50 25" stroke="currentColor" stroke-width="2"/>',
    clav: '<rect x="4" y="20" width="56" height="20" rx="0" fill="none" stroke="currentColor" stroke-width="2"/><path d="M4 30 H 60 M12 30 V 40 M20 30 V 40 M28 30 V 40 M36 30 V 40 M44 30 V 40 M52 30 V 40 M8 40 L 4 56 M56 40 L 60 56" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>',
    organ: '<rect x="10" y="10" width="44" height="36" rx="2" fill="none" stroke="currentColor" stroke-width="2"/><path d="M10 22 H 54 M10 34 H 54 M18 34 V 46 M26 34 V 46 M34 34 V 46 M42 34 V 46 M16 46 V 56 M24 46 V 52 M32 46 V 56 M40 46 V 52 M48 46 V 56" stroke="currentColor" stroke-width="2"/>',
    marimba: '<path d="M6 20 L 10 20 M14 20 L 18 20 M22 20 L 26 20 M30 20 L 34 20 M38 20 L 42 20 M46 20 L 50 20 M54 20 L 58 20 M8 20 V 40 M16 20 V 35 M24 20 V 45 M32 20 V 50 M40 20 V 45 M48 20 V 35 M56 20 V 30" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M4 48 L 60 48" stroke="currentColor" stroke-width="2"/>',
    vibes: '<rect x="6" y="20" width="52" height="24" rx="0" fill="none" stroke="currentColor" stroke-width="2"/><path d="M6 32 H 58 M14 20 V 32 M22 20 V 32 M30 20 V 32 M38 20 V 32 M46 20 V 32 M54 20 V 32 M6 44 H 58" stroke="currentColor" stroke-width="2"/>',
    guitar: '<path d="M32 10 L 32 22 M32 54 L 32 60 M22 22 C 15 22, 10 30, 10 40 C 10 55, 20 60, 32 60 C 44 60, 54 55, 54 40 C 54 30, 49 22, 42 22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="32" cy="36" r="6" stroke="currentColor" stroke-width="2"/><path d="M28 60 L 36 60" stroke="currentColor" stroke-width="2"/>',
    dist_guitar: '<path d="M25 60 L 40 40 L 55 10 L 50 15 L 35 45 L 20 50 L 15 60 Z M30 50 L 25 45 L 35 30 L 40 35" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>',
    flute: '<line x1="10" y1="50" x2="54" y2="14" stroke="currentColor" stroke-width="3" stroke-linecap="round"/><circle cx="20" cy="42" r="1.5" fill="currentColor"/><circle cx="28" cy="35" r="1.5" fill="currentColor"/><circle cx="36" cy="28" r="1.5" fill="currentColor"/><circle cx="44" cy="21" r="1.5" fill="currentColor"/>',
    harmonica: '<rect x="12" y="24" width="40" height="16" rx="4" fill="none" stroke="currentColor" stroke-width="2"/><path d="M12 30 H 52 M18 24 V 30 M26 24 V 30 M34 24 V 30 M42 24 V 30 M50 24 V 30" stroke="currentColor" stroke-width="2"/>',
    violin: '<path d="M32 60 V 10 M24 40 C 20 40, 20 50, 24 55 C 26 57, 30 57, 32 57 C 34 57, 38 57, 40 55 C 44 50, 44 40, 40 40 C 38 38, 38 32, 42 30 C 46 28, 46 20, 40 18 C 36 16, 28 16, 24 18 C 18 20, 18 28, 22 30 C 26 32, 26 38, 24 40 Z" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><line x1="48" y1="10" x2="48" y2="60" stroke="currentColor" stroke-width="2"/>',
    cello: '<path d="M32 50 V 10 M22 40 C 16 40, 16 50, 22 55 C 26 57, 30 57, 32 57 C 34 57, 38 57, 42 55 C 48 50, 48 40, 42 40 C 38 38, 38 32, 42 30 C 50 26, 50 18, 42 16 C 36 14, 28 14, 22 16 C 14 18, 14 26, 22 30 C 26 32, 26 38, 22 40 Z" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><line x1="32" y1="57" x2="32" y2="64" stroke="currentColor" stroke-width="2"/><line x1="28" y1="64" x2="36" y2="64" stroke="currentColor" stroke-width="2"/>',
    trumpet: '<path d="M10 26 L 25 26 L 35 18 L 35 34 L 25 26 M35 22 L 50 16 L 50 36 L 35 30 M38 22 V 16 M42 24 V 16 M46 26 V 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>',
    synth: '<rect x="6" y="20" width="52" height="24" rx="2" fill="none" stroke="currentColor" stroke-width="2"/><path d="M6 32 H 58 M14 32 V 44 M22 32 V 44 M30 32 V 44 M38 32 V 44 M46 32 V 44 M54 32 V 44 M10 24 H 20 V 28 H 10 Z" stroke="currentColor" stroke-width="2"/><circle cx="28" cy="26" r="2" fill="currentColor"/><circle cx="36" cy="26" r="2" fill="currentColor"/><circle cx="44" cy="26" r="2" fill="currentColor"/><circle cx="52" cy="26" r="2" fill="currentColor"/>',
    drums: '<ellipse cx="32" cy="45" rx="14" ry="6" fill="none" stroke="currentColor" stroke-width="2"/><path d="M18 45 V 30 M46 45 V 30 M18 30 Q 32 36 46 30" stroke="currentColor" stroke-width="2" fill="none"/><line x1="20" y1="45" x2="16" y2="60" stroke="currentColor" stroke-width="2"/><line x1="44" y1="45" x2="48" y2="60" stroke="currentColor" stroke-width="2"/><line x1="10" y1="20" x2="25" y2="35" stroke="currentColor" stroke-width="2"/><ellipse cx="10" cy="20" rx="6" ry="2" stroke="currentColor" stroke-width="2" fill="none"/><line x1="56" y1="20" x2="46" y2="35" stroke="currentColor" stroke-width="2"/><ellipse cx="56" cy="20" rx="6" ry="2" stroke="currentColor" stroke-width="2" fill="none"/>'
};

// =============================================================================
// DIAMOND SHAPE PARAMETERS
// =============================================================================

export const DIAMOND_PARAMS = {
    SIZE_H: 2.0,
    SIZE_W: 1.4,
    CORNER_V: 5.0,
    CORNER_H: 5.0,
    ROW_SHIFT: 0,
    SPLIT_AMP: 0.5,
    SPLIT_OFFSET_Y: 0,
    LABEL_OFFSET_X: -10,
    LABEL_OFFSET_Y: 0
};

// =============================================================================
// MINIMAP CONSTANTS
// =============================================================================

export const MINIMAP_TOTAL_COLS = 42.5;

// =============================================================================
// SECRET CODE (Developer Mode)
// =============================================================================

export const SECRET_CODE = ['D', 'C', 'C', 'A', 'B', 'E', 'C#', 'C', 'A'];

// =============================================================================
// IndexedDB Configuration
// =============================================================================

export const DB_CONFIG = {
    name: 'JankoPianoAudioDB',
    version: 1,
    storeName: 'soundfonts'
};
